import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CountryFlag, hasTeamArt, teamArtSlotPx } from '@/components/ui/CountryFlag';
import type { PropData, MyPropData } from '@/services/events/main-event';
import { asset } from '@/lib/asset';
import { fmtPts } from './utils';


// ============================================================================
// COUNTDOWN TIMER
// ============================================================================

/**
 * "Lines refresh in Xh Ym" — countdown to the next books poll for this week's
 * unbought props (line-refresh.service). Hidden once due; the next data fetch
 * re-derives the following slot.
 */
export function LineRefreshCountdown({ refreshAt }: { refreshAt: string | Date | null }) {
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (!refreshAt) return;
    function update() {
      const diff = new Date(refreshAt as string | Date).getTime() - Date.now();
      if (diff <= 0) {
        setLabel('');
        return;
      }
      const totalMinutes = Math.ceil(diff / 60_000);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      setLabel(h > 0 ? `${h}h ${m}m` : `${m}m`);
    }
    update();
    const t = setInterval(update, 30_000);
    return () => clearInterval(t);
  }, [refreshAt]);

  if (!refreshAt || !label) return null;
  return (
    <span className="text-[10px] uppercase tracking-wider text-tertiary">
      Lines refresh in <span className="text-warm-gray font-semibold tabular-nums">{label}</span>
    </span>
  );
}

export function CountdownTimer({ lockTime }: { lockTime: string | Date }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    function update() {
      const now = Date.now();
      const lock = new Date(lockTime).getTime();
      const diff = lock - now;

      if (diff <= 0) {
        setTimeLeft('Locked');
        setIsExpired(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    }

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [lockTime]);

  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-mono',
      isExpired
        ? 'bg-oxblood/10 border-oxblood/30 text-oxblood-text'
        : 'bg-gold/5 border-gold/20 text-gold'
    )}>
      {isExpired ? (
        <Lock className="w-3.5 h-3.5" />
      ) : (
        <Image src={asset('assets/icons/pocket_watch.webp')} alt="" width={16} height={16} />
      )}
      <span>{isExpired ? 'Picks locked' : `Locks in ${timeLeft}`}</span>
    </div>
  );
}


// ============================================================================
// SCORE BANNER
// ============================================================================

export function ScoreBanner({
  pickPoints, propBonusPoints, correctPicks, settledPicks, totalPicks,
  hitProps, settledMyProps, totalMyProps, isCompleted, accuracyModifier,
  pointsPerCorrectPick, totalScore,
}: {
  pickPoints: number;
  propBonusPoints: number;
  correctPicks: number;
  settledPicks: number;
  totalPicks: number;
  hitProps: number;
  settledMyProps: number;
  totalMyProps: number;
  isCompleted: boolean;
  accuracyModifier: number;
  pointsPerCorrectPick: number;
  totalScore: number;
}) {
  const displayScore = isCompleted ? totalScore : pickPoints + propBonusPoints;

  return (
    <div className={cn(
      'rounded-xl border p-3 mb-6',
      isCompleted
        ? 'bg-surface-2 border-verdigris/30'
        : 'bg-surface-2 border-gold/20',
    )}>
      {/* Main score row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Playfair's default figures are proportional, so a live score
              ticking 8 -> 11 -> 104 reflows its own label. tabular-nums pins
              the digit advance width — the one place in the app where a serif
              number updates in place. */}
          <span className={cn(
            'text-xl font-bold font-serif tabular-nums',
            isCompleted ? 'text-verdigris-text' : 'text-gold',
          )}>
            {fmtPts(displayScore)} points
          </span>
          {isCompleted && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-verdigris-text bg-verdigris/10 px-2 py-0.5 rounded">
              Final
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          {settledPicks > 0 && (
            <span>Picks: <span className="text-cream font-medium">{correctPicks}/{settledPicks}</span></span>
          )}
          {settledMyProps > 0 && (
            <span>Props: <span className="text-cream font-medium">{hitProps}/{settledMyProps}</span></span>
          )}
          {settledPicks === 0 && settledMyProps === 0 && (
            <span className="text-warm-gray/60">Awaiting results</span>
          )}
        </div>
      </div>

      {/* Expanded breakdown on completion */}
      {isCompleted && (
        <div className="mt-2 pt-2 border-t border-surface-3 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-warm-gray">Pick points</span>
            <span className="text-cream">{fmtPts(pickPoints)} <span className="text-warm-gray/60">({correctPicks}/{totalPicks} × {pointsPerCorrectPick} points)</span></span>
          </div>
          {totalMyProps > 0 && (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-warm-gray">Prop bonus</span>
                <span className="text-cream">+{fmtPts(propBonusPoints)} <span className="text-warm-gray/60">({hitProps} hit)</span></span>
              </div>
              {accuracyModifier !== 1.0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-warm-gray">Accuracy modifier</span>
                  <span className={cn(
                    'font-medium',
                    accuracyModifier >= 1.0 ? 'text-verdigris-text' : 'text-oxblood-text',
                  )}>
                    ×{accuracyModifier.toFixed(1)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// PURCHASED PROPS SUMMARY
// ============================================================================

export function PurchasedPropTile({ prop, sport }: { prop: MyPropData; sport?: string }) {
  const isHit = prop.result === 'HIT';
  const isMiss = prop.result === 'MISS';
  const isResolved = isHit || isMiss;

  // Extract a short label from description
  // "Lionel Messi — Goals O/U 1.5" → "Messi Goals 1.5"
  // "Detroit Pistons -5.5 (spread)" → "Pistons -5.5"
  // "76ers @ Pacers — Total O/U 220.5" → "Total O/U 220.5"
  const shortLabel = (() => {
    // Player prop
    const playerMatch = prop.description.match(/^(.+?)\s+[—–-]\s+(.+?)\s+O\/U\s+([\d.]+)$/i);
    if (playerMatch) {
      const lastName = playerMatch[1].split(' ').pop();
      // The Points→Goals rewrite is soccer-speak — never apply it on NFL events.
      const cat = (sport === 'NFL' ? playerMatch[2] : playerMatch[2].replace('Points', 'Goals'))
        .replace('Rebounds', 'Reb')
        .replace('Assists', 'Ast');
      return `${lastName} ${cat} ${playerMatch[3]}`;
    }
    // Spread
    const spreadMatch = prop.description.match(/^(.+?)\s+([+-]?[\d.]+)\s*\(spread\)$/i);
    if (spreadMatch) {
      const teamShort = spreadMatch[1].split(' ').pop();
      return `${teamShort} ${spreadMatch[2]}`;
    }
    // Total
    const totalMatch = prop.description.match(/Total\s+O\/U\s+([\d.]+)/i);
    if (totalMatch) return `Total O/U ${totalMatch[1]}`;
    // Moneyline
    const mlMatch = prop.description.match(/^(.+?)\s+wins/i);
    if (mlMatch) {
      const teamShort = mlMatch[1].split(' ').pop();
      return `${teamShort} ML`;
    }
    return prop.description.slice(0, 20);
  })();

  return (
    <div className={cn(
      'rounded-lg border px-2.5 py-1.5 text-[10px] leading-tight min-w-[100px]',
      isHit && 'bg-verdigris/10 border-verdigris/30',
      isMiss && 'bg-oxblood/10 border-oxblood/30',
      !isResolved && 'bg-gold/5 border-gold/20',
    )}>
      <div className={cn(
        'font-medium truncate',
        isHit && 'text-verdigris-text',
        isMiss && 'text-oxblood-text',
        !isResolved && 'text-gold',
      )}>
        {shortLabel}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className={cn(
          'font-bold text-[11px]',
          isHit && 'text-verdigris-text',
          isMiss && 'text-oxblood-text',
          !isResolved && 'text-warm-gray/60',
        )}>
          {isHit ? `HIT +${fmtPts(prop.bonusPoints)}` : isMiss ? 'MISS' : `${prop.bonusMultiplier}x`}
        </span>
        {!isResolved && (
          <span className="text-warm-gray/40">{prop.costCoins}c</span>
        )}
      </div>
    </div>
  );
}


export function PurchasedPropsSummary({ myProps, purchasedCount, maxProps, sport }: {
  myProps: MyPropData[];
  purchasedCount: number;
  maxProps: number;
  sport?: string;
}) {
  if (myProps.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">
          My Prop Boosts
        </span>
        <span className="text-xs text-warm-gray">{purchasedCount}/{maxProps}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {myProps.map((prop) => (
          <PurchasedPropTile key={prop.undercardPropId} prop={prop} sport={sport} />
        ))}
      </div>
    </div>
  );
}


/** Standalone prop card for props without a game reference */
export function StandalonePropCard({ prop, purchased, onPurchase, disabled, purchasingPropId }: {
  prop: PropData;
  purchased: MyPropData | null;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const isResolved = purchased?.result === 'HIT' || purchased?.result === 'MISS';
  const purchasePending = purchasingPropId !== null;

  // Condensed for non-purchased when locked
  if (disabled && !purchased) {
    return (
      <div className="rounded-lg border border-surface-3/50 bg-surface-2/30 px-2.5 py-1.5 flex items-center justify-between">
        <span className="text-[10px] text-tertiary truncate mr-2">{prop.description}</span>
        <span className="text-[10px] text-tertiary whitespace-nowrap">{prop.bonusMultiplier}x</span>
      </div>
    );
  }

  return (
    <div className={cn(
      'p-4 rounded-xl border',
      purchased ? 'bg-surface-2/50 border-surface-3' : 'bg-surface-2 border-surface-3 hover:border-gold/30',
    )}>
      <p className="text-cream text-sm font-medium mb-2">{prop.description}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          <span className="flex items-center gap-1">
            <Image src={asset('assets/icons/coins.webp')} alt="" width={14} height={14} />
            {prop.costCoins}
          </span>
          <span>{prop.bonusMultiplier}x bonus</span>
        </div>

        {purchased ? (
          <span className={cn(
            'px-3 py-1 rounded-lg text-xs font-bold',
            isResolved
              ? purchased.result === 'HIT'
                ? 'bg-verdigris/20 text-verdigris-text'
                : 'bg-oxblood/20 text-oxblood-text'
              : 'bg-gold/10 text-gold'
          )}>
            {isResolved ? (purchased.result === 'HIT' ? `HIT +${fmtPts(purchased.bonusPoints)}` : 'MISS') : 'Purchased'}
          </span>
        ) : (
          <Button
            variant="coins"
            size="sm"
            disabled={disabled || purchasePending}
            onClick={() => onPurchase(prop.id)}
          >
            {purchasingPropId === prop.id ? 'Buying...' : 'Buy'}
          </Button>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// MANUAL PREDICTIONS (Yes/No or Buy-Only, admin-authored)
// ============================================================================

/**
 * Icon-slot width shared by every prediction row, so the text column starts at
 * the same x whether the row shows a helmet, a flag, or the matchup mark.
 */
const SLOT_PX = teamArtSlotPx('md');

/**
 * Compact inline row for a manual prediction. Used both inside UnifiedGameCard
 * (per-match predictions stack under Spread/Total) and in the round-wide section.
 * Keeps to a single horizontal layout: text on the left, controls on the right.
 */
export function InlineManualPropRow({
  prop,
  gameSettled,
  purchased,
  onPurchase,
  disabled,
  purchasingPropId,
}: {
  prop: PropData;
  gameSettled: boolean;
  purchased: MyPropData | null;
  onPurchase: (propId: string, userSelection?: 'YES' | 'NO') => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const isYesNo = prop.pickMode === 'YES_NO';
  const isResolved = purchased?.result === 'HIT' || purchased?.result === 'MISS';
  const userPicked = purchased?.userSelection ?? null;
  const purchasePending = purchasingPropId !== null;
  const isPurchasingCurrent = purchasingPropId === prop.id;
  const buttonsDisabled = disabled || purchasePending || !!purchased || gameSettled;

  // 2-step click confirmation: first click stages the side, second click commits.
  // Auto-cancels after 3s so a stale "Confirm?" can't lock the user mid-thought.
  // Mirrors the existing odds-based prop pattern (SpreadSection, TotalSection).
  const [pendingSide, setPendingSide] = useState<'YES' | 'NO' | 'BUY' | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPending = useCallback(() => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = null;
    setPendingSide(null);
  }, []);
  useEffect(() => () => clearPending(), [clearPending]);
  // Cancel pending state if the prop becomes purchased/locked from elsewhere.
  useEffect(() => {
    if (purchased || gameSettled || disabled) clearPending();
  }, [purchased, gameSettled, disabled, clearPending]);

  const handleClick = (side: 'YES' | 'NO' | 'BUY') => {
    if (pendingSide === side) {
      clearPending();
      onPurchase(prop.id, side === 'BUY' ? undefined : side);
      return;
    }
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setPendingSide(side);
    confirmTimerRef.current = setTimeout(() => setPendingSide(null), 3000);
  };

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-3/20 @max-[14rem]:flex-col @max-[14rem]:items-stretch">
      <div className="flex items-start gap-2 flex-1 min-w-0">
      {/* ONE slot for every row, sized to the largest art (the NFL helmet,
          which renders 1.3x a flag tile) so the text column starts at the same
          x whatever lands inside. A team prop shows that team's art; a
          game-level prop shows the matchup mark. Sizing this to the flag
          instead left helmet rows 8px wider — the stagger this prevents. */}
      <span
        className="mt-0.5 self-start shrink-0 grid place-items-center"
        style={{ width: SLOT_PX, height: SLOT_PX }}
      >
        {hasTeamArt(prop.subjectTeam) ? (
          <CountryFlag name={prop.subjectTeam} size="md" />
        ) : (
          <Image
            src={asset('assets/icons/vs_transparent.png')}
            alt=""
            width={22}
            height={22}
            /* Same sepia hue as the helmets but darker in the source art —
               measured avg opaque RGB(123,109,86) vs the helmets (179,155,118),
               ~69% as bright. brightness() scales channels equally so the hue
               survives; 1.45 lands it on (178,158,125). No opacity: helmets
               render at full strength, and dimming this was what made the mark
               read as a different material rather than a quieter one. */
            style={{ filter: 'brightness(1.45)' }}
          />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-cream/90 leading-tight line-clamp-2">{prop.description}</p>
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-warm-gray/50">
          <span>{prop.bonusMultiplier}x</span>
          <span>·</span>
          <span>{prop.costCoins}c</span>
          {pendingSide && !purchased && (
            <>
              <span>·</span>
              <span className="text-gold font-bold">Tap again to confirm</span>
            </>
          )}
          {isPurchasingCurrent && (
            <>
              <span>·</span>
              <span className="text-gold font-bold">Buying...</span>
            </>
          )}
          {isResolved && (
            <>
              <span>·</span>
              <span
                className={cn(
                  'font-bold',
                  purchased!.result === 'HIT' ? 'text-verdigris-text' : 'text-oxblood-text'
                )}
              >
                {purchased!.result === 'HIT' ? `HIT +${fmtPts(purchased!.bonusPoints)}` : 'MISS'}
              </span>
            </>
          )}
        </div>
      </div>
      </div>

      {isYesNo ? (
        <div className="flex items-center gap-1 shrink-0 @max-[14rem]:w-full @max-[14rem]:justify-end @max-[14rem]:mt-1">
          <button
            type="button"
            aria-pressed={userPicked === 'YES'}
            aria-label={
              userPicked === 'YES'
                ? 'Your pick: Yes'
                : pendingSide === 'YES'
                  ? 'Tap again to confirm Yes'
                  : 'Pick Yes'
            }
            onClick={() => handleClick('YES')}
            disabled={buttonsDisabled}
            className={cn(
              'h-7 w-12 rounded text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              userPicked === 'YES'
                ? isResolved
                  ? purchased!.result === 'HIT'
                    ? 'bg-verdigris/30 text-verdigris-text border border-verdigris'
                    : 'bg-oxblood/30 text-oxblood-text border border-oxblood'
                  : 'bg-gold/30 text-gold border border-gold'
                : pendingSide === 'YES'
                  ? 'bg-gold/40 text-gold border border-gold ring-1 ring-gold animate-pulse'
                  : purchased || gameSettled
                    ? 'bg-surface-3/30 text-warm-gray/30 border border-surface-3 cursor-not-allowed'
                    : 'bg-verdigris/10 text-verdigris-text hover:bg-verdigris/20 border border-verdigris/30 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {pendingSide === 'YES' ? '✓ YES' : 'YES'}
          </button>
          <button
            type="button"
            aria-pressed={userPicked === 'NO'}
            aria-label={
              userPicked === 'NO'
                ? 'Your pick: No'
                : pendingSide === 'NO'
                  ? 'Tap again to confirm No'
                  : 'Pick No'
            }
            onClick={() => handleClick('NO')}
            disabled={buttonsDisabled}
            className={cn(
              'h-7 w-12 rounded text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              userPicked === 'NO'
                ? isResolved
                  ? purchased!.result === 'HIT'
                    ? 'bg-verdigris/30 text-verdigris-text border border-verdigris'
                    : 'bg-oxblood/30 text-oxblood-text border border-oxblood'
                  : 'bg-gold/30 text-gold border border-gold'
                : pendingSide === 'NO'
                  ? 'bg-gold/40 text-gold border border-gold ring-1 ring-gold animate-pulse'
                  : purchased || gameSettled
                    ? 'bg-surface-3/30 text-warm-gray/30 border border-surface-3 cursor-not-allowed'
                    : 'bg-oxblood/10 text-oxblood-text hover:bg-oxblood/20 border border-oxblood/30 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {pendingSide === 'NO' ? '✓ NO' : 'NO'}
          </button>
        </div>
      ) : (
        <div className="shrink-0 @max-[14rem]:w-full @max-[14rem]:flex @max-[14rem]:justify-end @max-[14rem]:mt-1">
          {purchased ? (
            <span className="px-2 py-1 rounded text-[10px] font-bold bg-gold/10 text-gold">Bought</span>
          ) : (
            <button
              type="button"
              onClick={() => handleClick('BUY')}
              disabled={buttonsDisabled}
              className={cn(
                'h-7 px-3 rounded text-[10px] font-bold border disabled:opacity-50 disabled:cursor-not-allowed',
                pendingSide === 'BUY'
                  ? 'bg-gold/40 text-gold border-gold ring-1 ring-gold animate-pulse'
                  : 'bg-gold/10 text-gold hover:bg-gold/20 border-gold/30'
              )}
            >
              {isPurchasingCurrent ? 'Buying...' : pendingSide === 'BUY' ? '✓ Confirm' : 'Buy'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
